import React from 'react';

import { DndContext, type DragEndEvent, PointerSensor, closestCenter, useSensor, useSensors } from '@dnd-kit/core';
import { SortableContext, arrayMove, useSortable, verticalListSortingStrategy } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';

import DragIndicatorIcon from '@mui/icons-material/DragIndicator';
import { Box, Checkbox, Chip, FormControlLabel, Paper, Stack, Typography } from '@mui/material';

import type { MarketingRouterRuleSignificanceMetric } from '~/types/marketingRouterRule';

import MarketingRouterTermLabel from './MarketingRouterTermLabel';

type Metric = MarketingRouterRuleSignificanceMetric;

export const SIGNIFICANCE_METRICS: Array<{ value: Metric; label: string }> = [
  { value: 'ttv', label: 'TTV' },
  { value: 'margin', label: 'Margin' },
  { value: 'purchase', label: 'Conversion' },
];

function getMetricLabel(metric: Metric): string {
  return SIGNIFICANCE_METRICS.find((option) => option.value === metric)?.label ?? metric;
}

function SortableMetricTile({ metric, position }: { metric: Metric; position: number }) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: metric });

  return (
    <Paper
      ref={setNodeRef}
      variant="outlined"
      sx={{
        alignItems: 'center',
        cursor: 'grab',
        display: 'flex',
        gap: 1.5,
        opacity: isDragging ? 0.6 : 1,
        p: 1.5,
        transform: CSS.Transform.toString(transform),
        transition,
        maxWidth: 360,
      }}
      {...attributes}
      {...listeners}
    >
      <DragIndicatorIcon color="action" />
      <Typography variant="body2" color="text.secondary" sx={{ minWidth: 16 }}>
        {position}
      </Typography>
      <Typography variant="body1">{getMetricLabel(metric)}</Typography>
      {position === 1 ? (
        <MarketingRouterTermLabel term="primaryMetric">
          <Chip label="Primary" size="small" color="primary" variant="outlined" />
        </MarketingRouterTermLabel>
      ) : null}
    </Paper>
  );
}

/**
 * Picks the significance metrics and their order.
 *
 * Order is the point of the field — it decides which metric settles the
 * experiment — so it is set by dragging the chosen metrics into place rather
 * than by nudging rows with arrows.
 */
export default function MarketingRouterMetricsField({
  value,
  onChange,
}: {
  value: Array<Metric>;
  onChange: (metrics: Array<Metric>) => void;
}) {
  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 4 } }));

  const toggleMetric = (metric: Metric) => {
    onChange(value.includes(metric) ? value.filter((selected) => selected !== metric) : [...value, metric]);
  };

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    if (over == null || active.id === over.id) {
      return;
    }
    const from = value.indexOf(active.id as Metric);
    const to = value.indexOf(over.id as Metric);
    if (from === -1 || to === -1) {
      return;
    }
    onChange(arrayMove(value, from, to));
  };

  return (
    <Stack spacing={2}>
      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1}>
        {SIGNIFICANCE_METRICS.map((metric) => (
          <FormControlLabel
            key={metric.value}
            control={<Checkbox checked={value.includes(metric.value)} onChange={() => toggleMetric(metric.value)} />}
            label={metric.label}
          />
        ))}
      </Stack>
      {value.length ? (
        <Box>
          <Typography color="text.secondary" variant="body2" gutterBottom>
            Drag the metric that should decide the outcome into first place. The rest are secondary, and their order
            among themselves does not change the result.
          </Typography>
          <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
            <SortableContext items={value} strategy={verticalListSortingStrategy}>
              <Stack spacing={1}>
                {value.map((metric, index) => (
                  <SortableMetricTile key={metric} metric={metric} position={index + 1} />
                ))}
              </Stack>
            </SortableContext>
          </DndContext>
        </Box>
      ) : (
        <Typography color="text.secondary" variant="body2">
          Select at least one metric.
        </Typography>
      )}
    </Stack>
  );
}
